import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Hybrid DatePicker — calendar selection AND manual typing, both writing the
 * same canonical `YYYY-MM-DD` ISO string to `onChange`.
 *
 * Design rules (user-spec, non-negotiable):
 *   - Manual typing is always available (resilience fallback).
 *   - Calendar popover stays available via the calendar icon on the right.
 *   - All accepted input is normalized to YYYY-MM-DD on blur or calendar pick.
 *   - Validation is non-blocking while typing — only flagged on blur.
 *   - Public prop API (`value`, `onChange`, `disabled`, `testId`, `minDate`,
 *     `maxDate`, `placeholder`, `className`) is unchanged from prior version.
 */
const ACCEPTED_FORMATS = [
  "yyyy-MM-dd",      // ISO (preferred)
  "yyyy/MM/dd",
  "MM/dd/yyyy",      // US
  "dd/MM/yyyy",      // EU
  "MM-dd-yyyy",
  "dd-MM-yyyy",
  "yyyyMMdd",
  "MMMM d yyyy",     // "April 6 2026"
  "MMM d yyyy",      // "Apr 6 2026"
];

const tryParseDate = (str) => {
  if (!str || typeof str !== "string") return null;
  const trimmed = str.trim();
  if (!trimmed) return null;
  for (const fmt of ACCEPTED_FORMATS) {
    const d = parse(trimmed, fmt, new Date());
    if (isValid(d)) return d;
  }
  // Last-ditch: native Date constructor (handles ISO + many native strings).
  const native = new Date(trimmed);
  return isValid(native) ? native : null;
};

const toISO = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export function DatePicker({
  value,
  onChange,
  placeholder = "YYYY-MM-DD",
  className,
  disabled = false,
  testId,
  minDate,
  maxDate,
}) {
  // Local mirror of the input text — lets the user type freely without
  // every keystroke round-tripping through the parent's controlled state.
  const [text, setText] = React.useState(value || "");
  const [error, setError] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  // Keep local text in sync if the parent changes the value externally
  // (e.g. form reset, calendar pick from elsewhere).
  React.useEffect(() => {
    setText(value || "");
    setError(false);
  }, [value]);

  const parsedValueDate = React.useMemo(() => {
    if (!value) return undefined;
    const d = tryParseDate(value);
    return d || undefined;
  }, [value]);

  const handleTextChange = (e) => {
    const next = e.target.value;
    setText(next);
    if (error) setError(false);
    // If the user has typed a complete canonical ISO string, push it up
    // immediately so any sibling form state stays current. Other formats
    // wait for blur (avoids thrashing while mid-typing "1" → "12" → "12/").
    if (/^\d{4}-\d{2}-\d{2}$/.test(next)) {
      const d = tryParseDate(next);
      if (d) onChange(toISO(d));
    } else if (next === "") {
      onChange("");
    }
  };

  const handleBlur = () => {
    const trimmed = (text || "").trim();
    if (!trimmed) {
      onChange("");
      setError(false);
      return;
    }
    const d = tryParseDate(trimmed);
    if (d) {
      // Range gates (minDate / maxDate) — silently clamp by flagging error
      // rather than mutating; user can correct.
      const min = minDate ? tryParseDate(minDate) : null;
      const max = maxDate ? tryParseDate(maxDate) : null;
      if ((min && d < min) || (max && d > max)) {
        setError(true);
        return;
      }
      const iso = toISO(d);
      onChange(iso);
      setText(iso);
      setError(false);
    } else {
      setError(true);
      // Don't clear — preserve the user's typing so they can correct it.
    }
  };

  const handleCalendarSelect = (date) => {
    if (!date) {
      onChange("");
      setText("");
    } else {
      const iso = toISO(date);
      onChange(iso);
      setText(iso);
    }
    setError(false);
    setOpen(false);
  };

  return (
    <div className={cn("relative", className)} data-testid={testId ? `${testId}-wrapper` : undefined}>
      <Input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={testId}
        aria-invalid={error || undefined}
        className={cn(
          "pr-10 h-10",
          error && "border-amber-500 focus-visible:ring-amber-500"
        )}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={testId ? `${testId}-calendar-btn` : undefined}
            aria-label="Open calendar"
            className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CalendarIcon className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={parsedValueDate}
            defaultMonth={parsedValueDate}
            onSelect={handleCalendarSelect}
            disabled={(d) => {
              if (minDate) {
                const m = tryParseDate(minDate);
                if (m && d < m) return true;
              }
              if (maxDate) {
                const m = tryParseDate(maxDate);
                if (m && d > m) return true;
              }
              return false;
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {error && (
        <p
          className="mt-1 text-[11px] text-amber-600 dark:text-amber-400"
          data-testid={testId ? `${testId}-error` : undefined}
          role="status"
        >
          Use YYYY-MM-DD (e.g. {format(new Date(), "yyyy-MM-dd")})
        </p>
      )}
    </div>
  );
}

export default DatePicker;
